import { useLayoutContext } from '@/contexts';
import { Alert, Card, Flex, Text } from '@mantine/core';
import { IconAlertCircle } from '@tabler/icons-react';
import { useCallback, useMemo } from 'react';
import {
	ContentSectionAlignment,
	ContentSectionLocation,
	ContentSectionPlacement,
} from '../../../../backend/src/types.generated';
import { MarkdownComponent } from '../markdown';
import { useGetSectionContentQuery } from './graphql.generated';

const MOCK_CONTENT = `
Here is where the content field text will render.

**Take Note:**

- This is a mock content
- This is a mock content
`;

const MOCK_SECTION = {
	title: 'Section Title',
	subheading: 'Here is the section subtitle',
	content: MOCK_CONTENT,
};

export const CustomContentSection = ({
	location,
	placement,
}: {
	location: ContentSectionLocation;
	placement: ContentSectionPlacement;
}) => {
	const { debugCMS, cmsAlignment, cmsStyle } = useLayoutContext();
	const { data, loading, error } = useGetSectionContentQuery({
		variables: {
			input: {
				location,
				placement,
			},
		},
	});
	const textAlign = useMemo(() => {
		if (data?.getSectionContent?.[0]?.alignment === ContentSectionAlignment.Left) return 'left';
		if (data?.getSectionContent?.[0]?.alignment === ContentSectionAlignment.Right) return 'right';
		return 'center';
	}, [data?.getSectionContent]);
	const MockSection = useCallback(() => {
		return (
			<>
				{cmsStyle === 'Text' && (
					<Flex direction="column" w="100%" style={{ textAlign: cmsAlignment }}>
						<Flex direction="column">
							<Text size="lg" fw={900}>
								{MOCK_SECTION.title}
							</Text>

							<Text opacity={0.6} size="md" fw={600}>
								{MOCK_SECTION.subheading}
							</Text>
						</Flex>

						<MarkdownComponent content={MOCK_SECTION.content} />
					</Flex>
				)}
				{cmsStyle === 'Card' && (
					<Card withBorder radius="md" w="100%" style={{ textAlign: cmsAlignment }}>
						<Flex direction="column">
							<Text size="lg" fw={900}>
								{MOCK_SECTION.title}
							</Text>
							<Text opacity={0.6} size="md" fw={600}>
								{MOCK_SECTION.subheading}
							</Text>
						</Flex>

						<MarkdownComponent content={MOCK_SECTION.content} />
					</Card>
				)}
				{cmsStyle === 'Warning' && (
					<Alert
						variant="filled"
						w="100%"
						icon={<IconAlertCircle />}
						title={MOCK_SECTION.title}
						style={{ textAlign: cmsAlignment }}
						color="red"
					>
						<Flex direction="column">
							<Text opacity={0.6} size="sm" fw={600}>
								{MOCK_SECTION.subheading}
							</Text>
						</Flex>

						<MarkdownComponent content={MOCK_SECTION.content} />
					</Alert>
				)}
			</>
		);
	}, [cmsStyle, cmsAlignment]);
	if (debugCMS) {
		return <MockSection />;
	}
	if (loading) return null;
	if (error) return null;

	const section = data?.getSectionContent?.[0];
	if (!section) return null;

	return (
		<Flex direction="column" w="100%" style={{ textAlign }}>
			{Boolean(section.title || section.subheading) && (
				<Flex direction="column">
					{section.title && (
						<Text size="lg" fw={900}>
							{section.title}
						</Text>
					)}
					{section.subheading && (
						<Text opacity={0.6} size="md" fw={600}>
							{section.subheading}
						</Text>
					)}
				</Flex>
			)}

			<MarkdownComponent content={section.content} />
		</Flex>
	);
};
